// NewListModal.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlassMartiniAlt, faLock, faLockOpen, faTimes, faList } from '@fortawesome/free-solid-svg-icons';

function NewListModal({ isOpen, onClose, onCreate, userData, apiClient, scrollContainerRef }) {
  const [listName, setListName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [listTypeId, setListTypeId] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      if (isOpen) {
        scrollContainer.style.overflow = 'hidden';
        document.body.classList.add('modal-open'); // For touch prevention
      } else {
        scrollContainer.style.overflow = 'auto';
        document.body.classList.remove('modal-open');
      }
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.style.overflow = 'auto';
      }
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, scrollContainerRef]);

  const handleCreate = async () => {
    if (!listName.trim()) {
      setError('List name is required');
      return;
    }

    try {
      const data = await apiClient.request('/api/lists', {
        method: 'POST',
        body: JSON.stringify({
          listname: listName,
          description,
          public: isPublic,
          listtypeid: listTypeId,
          userid: userData.userid,
        }),
      });
      onCreate(data);
      setListName('');
      setDescription('');
      setIsPublic(true);
      setListTypeId(1);
      setError('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create list');
      if (err.message === 'Failed to refresh token') {
        onClose();
        // Logout is handled by the parent component (ListsPage)
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-container">
        <div className="modal-header">
          <h3>Create New List</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-body">
          {error && <p className="error-message text-red-500 mb-4">{error}</p>}
          <div className="form-group">
            <label htmlFor="list-name">List Name</label>
            <input
              id="list-name"
              type="text"
              placeholder="Enter a name for your list"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label htmlFor="list-description">Description (optional)</label>
            <textarea
              id="list-description"
              rows="3"
              placeholder="Add a short description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>List Type</label>
            <div className="privacy-options">
              <div className="privacy-option">
                <button
                  type="button"
                  className={`privacy-label ${listTypeId === 1 ? 'active' : ''}`}
                  onClick={() => setListTypeId(1)}
                >
                  <FontAwesomeIcon icon={faGlassMartiniAlt} className="mr-2" />
                  <div>
                    <p>Cocktails</p>
                    <p className="text-sm">Drinks you love</p>
                  </div>
                </button>
              </div>
              <div className="privacy-option">
                <button
                  type="button"
                  className={`privacy-label ${listTypeId === 2 ? 'active' : ''}`}
                  onClick={() => setListTypeId(2)}
                >
                  <FontAwesomeIcon icon={faList} className="mr-2" />
                  <div>
                    <p>Ingredients</p>
                    <p className="text-sm">Items to track</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Privacy</label>
            <div className="privacy-options">
              <div className="privacy-option">
                <button
                  type="button"
                  className={`privacy-label ${isPublic ? 'active' : ''}`}
                  onClick={() => setIsPublic(true)}
                >
                  <FontAwesomeIcon icon={faLockOpen} className="mr-2" />
                  <div>
                    <p>Public</p>
                    <p className="text-sm">Anyone can view</p>
                  </div>
                </button>
              </div>
              <div className="privacy-option">
                <button
                  type="button"
                  className={`privacy-label ${!isPublic ? 'active' : ''}`}
                  onClick={() => setIsPublic(false)}
                >
                  <FontAwesomeIcon icon={faLock} className="mr-2" />
                  <div>
                    <p>Private</p>
                    <p className="text-sm">Only you can view</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="create-btn" onClick={handleCreate}>
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewListModal;